/**
 * Supervision tab — everything about working under a supervisor in one place:
 *   - Note co-signing requirement + assigned supervisor (commit via footer Save)
 *   - Supervisory claim submission (autosaves; bill-under NPI overrides)
 *
 * The supervisor assignment scopes whose cosign queue this member's notes
 * appear in. Unassigned members who require co-signature fall back to every
 * supervisor's queue. When the member already bills under a supervisor-role
 * member (GDN-1926), that person is suggested as a one-click default.
 */

import { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { MemberBillingInfo } from '@store/generated/organizations';
import { ROLES } from '@utils/roleUtils';

import { useGetOrganizationMembersQuery } from '../../store/api';

import { ClaimSubmissionSettings } from './ClaimSubmissionSettings';

const UNASSIGNED = '';

interface SupervisionSectionProps {
  userId: string;
  billingInfo?: MemberBillingInfo | null;
  isClinician: boolean;
  needsCosign: boolean;
  onNeedsCosignChange: (value: boolean) => void;
  supervisorUserId: string;
  onSupervisorChange: (value: string) => void;
  disabled?: boolean;
}

export function SupervisionSection({
  userId,
  billingInfo,
  isClinician,
  needsCosign,
  onNeedsCosignChange,
  supervisorUserId,
  onSupervisorChange,
  disabled = false,
}: SupervisionSectionProps) {
  const { data: membersData } = useGetOrganizationMembersQuery({});
  const { clinicians } = useEventTracking();

  const changeSupervisor = (value: string, seededFromBillUnder: boolean) => {
    onSupervisorChange(value);
    clinicians.memberSupervisorChanged({
      member_id: userId,
      has_supervisor: value !== UNASSIGNED,
      seeded_from_bill_under: seededFromBillUnder,
    });
  };

  // Only members holding the Supervisor role can be assigned — the backend
  // rejects anyone else.
  const supervisorOptions = useMemo(
    () =>
      (membersData?.members ?? [])
        .filter(
          m =>
            m.user_id !== userId &&
            m.status === 'active' &&
            (m.roles ?? []).includes(ROLES.SUPERVISOR),
        )
        .map(m => ({ userId: m.user_id, label: m.name || m.email || m.user_id })),
    [membersData, userId],
  );

  // Suggest the bill-under target (claim submission) when it's a valid
  // supervisor and no assignment exists yet — the two usually coincide.
  const billUnderId = billingInfo?.bill_under_user_id ?? '';
  const suggestion = useMemo(() => {
    if (!billUnderId || supervisorUserId) return null;
    return supervisorOptions.find(option => option.userId === billUnderId) ?? null;
  }, [billUnderId, supervisorUserId, supervisorOptions]);

  return (
    <Box>
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          Note Co-Signing
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5, mb: 1 }}
        >
          Applies on Save.
        </Typography>
        {isClinician ? (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={needsCosign}
                  onChange={e => onNeedsCosignChange(e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Require co-signature</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Notes must be co-signed by a supervisor before being finalized
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />

            {needsCosign && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label="Co-signing supervisor"
                  value={supervisorUserId}
                  onChange={e => changeSupervisor(e.target.value, false)}
                  disabled={disabled}
                  helperText={
                    supervisorUserId
                      ? 'Their notes appear only in this supervisor’s co-sign queue.'
                      : 'Unassigned: their notes appear in every supervisor’s co-sign queue.'
                  }
                >
                  <MenuItem value={UNASSIGNED}>
                    Unassigned — visible to all supervisors
                  </MenuItem>
                  {supervisorOptions.map(option => (
                    <MenuItem key={option.userId} value={option.userId}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                {suggestion && (
                  <Alert
                    severity="info"
                    sx={{ mt: 1.5 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        disabled={disabled}
                        onClick={() => changeSupervisor(suggestion.userId, true)}
                      >
                        Assign
                      </Button>
                    }
                  >
                    Their claims are submitted under {suggestion.label} — assign them as
                    the co-signing supervisor too?
                  </Alert>
                )}
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Co-signature requirements apply to members with the Clinician role.
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <ClaimSubmissionSettings userId={userId} billingInfo={billingInfo} disabled={disabled} />
    </Box>
  );
}
